import {
  CaptionCue,
  CaptionsRenderer,
  GOOEY_TEXT_DEFAULT_CANVAS_CONFIG,
} from '@sparemin/text-effects';
import memoizee from 'memoizee';
import React, { useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { createSelector } from 'reselect';
import { captionsStyleSelector } from 'redux/modules/embed/selectors';
import { Size } from 'types';
import { getDefaultCaptionWordStyling } from 'utils/caption-adv-animation';

type UseEditSceneCaptionsRendererProps = {
  // the container to render the captions into
  containerRef: React.RefObject<HTMLDivElement | null>;
  captionCues: CaptionCue[];
  previewDimensions: Size<number> | undefined;
  isFontsLoaded: boolean;
};

type UseEditSceneCaptionsRendererResult = {
  captionsRendererRef: React.MutableRefObject<CaptionsRenderer | undefined>;
};

const createCaptionsRendererConfigSelector = memoizee(
  (
    captionCues: CaptionCue[],
    width: number | undefined,
    height: number | undefined,
  ) =>
    createSelector([captionsStyleSelector], captionsStyle => {
      if (
        !captionsStyle ||
        !captionCues ||
        captionCues.length === 0 ||
        !width
      ) {
        return null;
      }

      const advancedAnimation = captionsStyle.get('advancedAnimation')?.toJS();
      const textStyle = captionsStyle.get('textStyle')?.toJS();
      const gooeyText = captionsStyle
        .get('advancedTextConfigs')
        ?.get('gooeyText')
        ?.toJS();

      if (!advancedAnimation?.enabled) {
        return null;
      }

      return {
        cues: captionCues,
        textStyles: textStyle,
        animationConfig: advancedAnimation,
        animatorOpts: {
          animeParams: {
            autoplay: false,
            loop: false,
          },
        },
        ...(width && height && { canvasDims: { width, height } }),
        ...getDefaultCaptionWordStyling(width),
        ...(gooeyText && {
          gooeyTextConfig: {
            baseConfig: gooeyText,
            opts: {
              canvasConfig: GOOEY_TEXT_DEFAULT_CANVAS_CONFIG,
            },
          },
        }),
      };
    }),
  { max: 1 },
);

export default function useEditSceneCaptionsRenderer({
  containerRef,
  captionCues,
  previewDimensions,
  isFontsLoaded,
}: UseEditSceneCaptionsRendererProps): UseEditSceneCaptionsRendererResult {
  const captionsRendererRef = useRef<CaptionsRenderer | undefined>();
  const captionsRendererConfig = useSelector(
    useMemo(
      () =>
        createCaptionsRendererConfigSelector(
          captionCues,
          previewDimensions?.width,
          previewDimensions?.height,
        ),
      [captionCues, previewDimensions?.width, previewDimensions?.height],
    ),
  );

  useEffect(() => {
    if (
      !isFontsLoaded ||
      !containerRef.current ||
      !captionsRendererConfig ||
      captionsRendererRef.current
    ) {
      return;
    }

    captionsRendererRef.current = new CaptionsRenderer(
      containerRef.current,
      captionsRendererConfig,
    );
  }, [captionsRendererConfig, containerRef, isFontsLoaded]);

  useEffect(() => {
    if (!captionsRendererRef.current || !captionsRendererConfig) {
      return;
    }

    requestAnimationFrame(() => {
      captionsRendererRef.current?.updateConfig(captionsRendererConfig);
    });
  }, [captionsRendererConfig]);

  return {
    captionsRendererRef,
  };
}
